import type { AmbientSource } from '@/features/ambient/useAmbientAudio';

export interface RestaurantTheme {
  primary: string;
  primaryDeep: string;
  primaryTeal: string;
  accent: string;
  footerGradientFrom: string;
  footerGradientTo: string;
  headerBarBackground?: string;
  pageBackground?: string;
  cardBackground?: string;
  textPrimary?: string;
  textSecondary?: string;
  borderColor?: string;
  styleMode?: 'playful' | 'premium';
  siteCommandeLogoVariant?: LogoVariantPreference;
  showcaseLogoVariant?: LogoVariantPreference;
  ouilotoLogoVariant?: LogoVariantPreference;
  siteCommandeFaviconVariant?: LogoVariantPreference;
  showcaseFaviconVariant?: LogoVariantPreference;
  ouilotoFaviconVariant?: LogoVariantPreference;
}

export type LogoVariantPreference = 'auto' | 'light' | 'dark';

export const DAY_KEYS = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
] as const;

export type DayKey = typeof DAY_KEYS[number];

export interface TimeRange {
  open: string;
  close: string;
}

export type WeeklyHours = Record<DayKey, TimeRange[]>;

export interface Closure {
  from: string;   // 'YYYY-MM-DD'
  to: string;     // 'YYYY-MM-DD'
  label?: string;
}

export function emptyWeeklyHours(): WeeklyHours {
  return {
    monday: [],
    tuesday: [],
    wednesday: [],
    thursday: [],
    friday: [],
    saturday: [],
    sunday: [],
  };
}

export type OrderMode = 'takeaway' | 'delivery' | 'dine_in';

/** Stable Hub restaurant classification. It is descriptive only and never gates ordering. */
export const RESTAURANT_TYPE_VALUES = [
  'unspecified',
  'chinese',
  'japanese',
  'korean',
  'vietnamese',
  'thai',
  'southeast_asian',
  'indian',
  'middle_eastern',
  'mediterranean',
  'italian',
  'french',
  'iberian',
  'african',
  'latin_american',
  'american',
  'international',
  'fusion',
  'bistro',
  'fine_dining',
  'fast_food',
  'pizzeria',
  'burger',
  'kebab',
  'grill_steakhouse',
  'seafood',
  'vegetarian_vegan',
  'buffet',
  'brunch',
  'cafe',
  'bakery',
  'dessert_ice_cream',
  'bubble_tea',
  'bar_pub',
  'food_truck',
  'catering',
  'canteen_cafeteria',
  'other',
] as const;

export type RestaurantType = typeof RESTAURANT_TYPE_VALUES[number];

export function normalizeRestaurantType(value: string | undefined): RestaurantType {
  return (RESTAURANT_TYPE_VALUES as readonly string[]).includes(value ?? '')
    ? (value as RestaurantType)
    : 'unspecified';
}

export interface RestaurantHero {
  titleLine1: string;
  titleEm?: string;
  titleAccent?: string;
  ratingStars?: number;
  ratingLabel?: string;
  prepTime?: string;
  deliveryFee?: string;
  minOrder?: string;
  lead?: string;
  photo?: string;
  photoStamp?: string;
  photoTag?: string;
}

export interface DeliveryPartner {
  slug: string;
  name: string;
  url: string;
}

export interface RestaurantAnnouncement {
  id: string;
  greeting?: string;
  headline?: string;
  tagline?: string;
  lead?: string;       // rendered HTML from backend lead_doc
  closing?: string;    // rendered HTML from backend closing_doc
  image?: string;
  ctaLabel?: string;
  ctaHref?: string;
  /** DF-0428 — le popup dans la langue du client ; `body` en texte simple.
   *  Langue ou champ absent = le texte français. */
  translations?: AnnouncementTranslations;
}

export type ContentLanguage = 'en' | 'zh' | 'ja' | 'ar';

export type AnnouncementTranslations = Partial<
  Record<ContentLanguage, { headline?: string; body?: string; cta_label?: string }>
>;

/** DF-0428 — `{lang: [texte]}`, aligné par position sur `marqueeMessages`. */
export type MarqueeTranslations = Partial<Record<ContentLanguage, string[]>>;

export interface RestaurantPromo {
  tag: string;
  headline: string;
  description: string;
  priceText: string;
  originalPriceText?: string;
  photo?: string;
}

/** SC-1434 — canaux que le restaurateur met en pause un par un (clés du hub). */
export type PausableChannel = 'take_out' | 'delivery' | 'dine_in' | 'reservation';

export interface RestaurantIdentity {
  uuid: string;
  name: string;
  /** Always normalized by hubApi; optional for legacy/manual identity fixtures. */
  restaurantType?: RestaurantType;
  logoUrl: string | null;
  logoInverseUrl?: string | null;
  faviconUrl: string | null;
  faviconInverseUrl?: string | null;
  address: string | null;
  /** Servis à part de `address` : chaque surface compose sa ligne — DF-0132.
   *  Optionnels comme leurs voisins : les fixtures d'identité manuelles ne les
   *  portent pas, et une adresse sans code postal reste une adresse. */
  postalCode?: string | null;
  city?: string | null;
  phone: string | null;
  country: string;
  /** DF-0330 — fuseau IANA du restaurant (ex. "America/Cayenne"), resolu par
   *  le hub. Optionnel/nullable : un hub pas encore deploye ou une fixture
   *  d'identite manuelle ne le portent pas — `restaurantTz()` retombe alors
   *  sur `country`. */
  timezone?: string | null;
  hours: WeeklyHours;
  closures: Closure[];
  reservationClosures: Closure[];
  /** SC-1738 — weekly hours reservations are taken in, resolved by the hub
   *  (equal to `hours` when the restaurant set none). Absent from an older hub
   *  or a hand-made fixture: the reservation page falls back to `hours`. */
  reservationHours?: WeeklyHours;
  theme: RestaurantTheme;
  googleReviewLink?: string | null;
  instagramLink?: string | null;
  tiktokLink?: string | null;
  onlineOrderingEnabled: boolean;
  /** SC-1434 — ce que la pause coupe quand `onlineOrderingEnabled` est faux.
   *  Absent ou vide : tout (un hub d'avant le champ ne connaît que la pause
   *  complète). Se lit par `isChannelPaused`, jamais directement. */
  onlineOrderingPausedChannels?: PausableChannel[];
  onlineOrderingFeatureEnabled: boolean;
  /** SC-1090 — combien de jours à l'avance le client peut pré-commander (1 =
   *  seulement Aujourd'hui/Demain, l'horizon d'avant ce ticket). Le hub
   *  borne déjà à [1,14] et vaut 1 par défaut ; optionnel pour couvrir un hub
   *  pas encore déployé ou une fixture d'identité manuelle — voir
   *  `normalizePreorderMaxDays`. */
  preorderMaxDays?: number;
  /** DF-0371 — le plus grand groupe réservable en ligne (réglage SO-87 du
   *  back-office). Le hub borne à [1,50], 8 par défaut ; optionnel pour la
   *  même raison que `preorderMaxDays` — voir `normalizeReservationMaxPartySize`. */
  reservationMaxPartySize?: number;
  /** SC-1465 — premier créneau proposé, par mode. `auto` (ou absent : hub
   *  pas encore déployé, fixture) = ouverture + temps de préparation, le
   *  calcul d'avant ce réglage. Voir `normalizeFirstSlot`. */
  firstSlotEmporter?: FirstSlotConfig;
  firstSlotLivraison?: FirstSlotConfig;
  /** SC-1500 — option « Dès que possible » du choix d'horaire, par mode.
   *  Absent (hub plus ancien) = désactivée. */
  asapEmporter?: boolean;
  asapLivraison?: boolean;
}

/** SC-1465 — `manual` : heure du premier créneau saisie par le restaurateur,
 *  par jour et par service (`lunch` = plage ouverte avant 16 h, `dinner` =
 *  après). Une case absente garde le calcul automatique. */
export interface FirstSlotConfig {
  mode: 'auto' | 'manual';
  days: Partial<Record<DayKey, { lunch?: string; dinner?: string }>>;
}

const HM_RE = /^([01]\d|2[0-3]):[0-5]\d$/;

/** SC-1465 — même normalisation que le hub (`restaurants/first_slot.py`) :
 *  forme illisible -> auto, case mal formée écartée une par une. */
export function normalizeFirstSlot(raw: unknown): FirstSlotConfig {
  if (raw === null || typeof raw !== 'object' || Array.isArray(raw)) return { mode: 'auto', days: {} };
  const src = raw as Record<string, unknown>;
  const mode = src.mode === 'manual' ? 'manual' : 'auto';
  const daysIn = (src.days && typeof src.days === 'object' && !Array.isArray(src.days))
    ? src.days as Record<string, unknown>
    : {};
  const days: FirstSlotConfig['days'] = {};
  for (const day of DAY_KEYS) {
    const entry = daysIn[day];
    if (entry === null || typeof entry !== 'object') continue;
    const e = entry as Record<string, unknown>;
    const kept: { lunch?: string; dinner?: string } = {};
    if (typeof e.lunch === 'string' && HM_RE.test(e.lunch)) kept.lunch = e.lunch;
    if (typeof e.dinner === 'string' && HM_RE.test(e.dinner)) kept.dinner = e.dinner;
    if (kept.lunch || kept.dinner) days[day] = kept;
  }
  return { mode, days };
}

/** SC-1090 — normalise `preorderMaxDays` côté client aussi (hub plus ancien,
 *  fixture de test sans le champ, ou valeur invalide) : absent/NaN/hors
 *  bornes retombe sur 1, comme l'horizon J+1 d'avant ce ticket. */
export function normalizePreorderMaxDays(value: number | null | undefined): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) return 1;
  return Math.min(14, Math.max(1, Math.trunc(value)));
}

/** DF-0371 — absent/NaN retombe sur 8, ce que la page proposait en dur avant
 *  que le réglage lui parvienne ; hors bornes est ramené dans [1,50]. */
export function normalizeReservationMaxPartySize(value: number | null | undefined): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) return 8;
  return Math.min(50, Math.max(1, Math.trunc(value)));
}

export type PromotionAppliesTo = 'both' | 'emporter' | 'livraison';

export interface Promotion {
  id: string;
  label: string;
  minSubtotal: number;        // € TTC
  discountPercent: number;    // 1..100
  appliesTo: PromotionAppliesTo;
  validFrom: string | null;   // 'YYYY-MM-DD'
  validTo: string | null;
  active: boolean;
}

/**
 * SC-902 — un « service » (Entrées / Plats / Desserts…) : un groupe de
 * catégories, dans l'ordre d'envoi. `key` est un slug stable, `label` le nom
 * affiché, `categoryUuids` les catégories qui y appartiennent (design §1.2).
 */
export interface DineInCourse {
  key: string;
  label: string;
  categoryUuids: string[];
}

export interface RestaurantConfiguration {
  modes: OrderMode[];
  acceptsReservations: boolean;
  acceptsDineIn?: boolean;
  prepTimeEmporterMin: number;
  prepTimeLivraisonMin: number;
  marqueeMessages: string[];
  marqueeTranslations?: MarqueeTranslations;
  ambient: AmbientSource | null;
  deliveryPartners: DeliveryPartner[];
  announcement: RestaurantAnnouncement | null;
  hero: RestaurantHero | null;
  promo: RestaurantPromo | null;
  promotions: Promotion[];
  paymentOnlineEnabled: boolean;
  paymentOnlineDescription: string;
  paymentOnsiteEnabled: boolean;
  paymentOnsiteDescription: string;
  /** SC-554 — minimum de commande en centimes ; 0 = aucune restriction. */
  minOrderAmountEmporterCents?: number;
  minOrderAmountLivraisonCents?: number;
  /** SC-902 — « Envoi par service » : défaut éteint/vide, un serveur
   *  antérieur (ou un resource_api sans le champ) se comporte exactement
   *  comme avant (design §1.2). Optionnels comme `acceptsDineIn` /
   *  `minOrderAmountEmporterCents` ci-dessus : le transform les résout
   *  toujours (`?? false` / `?? []`), l'optionnalité n'est que pour les
   *  fixtures qui construisent une config partielle. */
  dineInCoursesEnabled?: boolean;
  dineInCourses?: DineInCourse[];
  /** SC-935 — « Emporter » depuis le ticket d'une table, en mode serveur.
   *  Interrupteur du restaurateur, **fermé par défaut** : sans lui la bascule
   *  n'existe pas dans l'en-tête du ticket, et sale_api refuse la tournée
   *  (403 `TABLE_EMPORTER_ROUND_DISABLED`). Optionnel pour la même raison que
   *  `dineInCoursesEnabled` : les fixtures construisent des configs
   *  partielles, le transform le résout toujours (`?? false`). */
  tableEmporterRoundEnabled?: boolean;
}
